import api from '../../utils/api';
import type {
  WhatsAppDeliveryLog,
  WhatsAppHubSettings,
  WhatsAppWebhookConfig,
  WhatsAppWebhookEventsResponse,
} from '../../models/MasterAdmin/whatsappHub';
import type { WhatsAppTemplateVariableMapping } from '../../models/MasterAdmin/whatsappHub';
import type { WhatsAppTemplate } from '../../models/MasterAdmin/whatsappCampaign';

const BASE = '/master-admin/whatsapp';

export type WhatsAppDeliveryLogsResponse = {
  success: boolean;
  data: {
    total: number;
    rows: WhatsAppDeliveryLog[];
    statusCounts?: import('../../models/MasterAdmin/whatsappHub').WhatsAppDeliveryStatusCounts;
  };
};

export type WhatsAppWebhookConfigResponse = {
  success: boolean;
  data: WhatsAppWebhookConfig;
};

export type WhatsAppHubSettingsResponse = {
  success: boolean;
  data: WhatsAppHubSettings;
};

export async function fetchWhatsAppDeliveryLogs(params?: {
  source?: string;
  status?: string;
  eventType?: string;
  campaignId?: string;
  search?: string;
  days?: number;
  limit?: number;
  offset?: number;
}): Promise<WhatsAppDeliveryLogsResponse> {
  const response = await api.get<WhatsAppDeliveryLogsResponse>(`${BASE}/delivery/logs`, { params });
  return response.data;
}

export async function fetchWhatsAppWebhookConfig(): Promise<WhatsAppWebhookConfigResponse> {
  const response = await api.get<WhatsAppWebhookConfigResponse>(`${BASE}/meta/webhooks`);
  return response.data;
}

export async function fetchWhatsAppHubSettings(): Promise<WhatsAppHubSettingsResponse> {
  const response = await api.get<WhatsAppHubSettingsResponse>(`${BASE}/meta/settings`);
  return response.data;
}

export async function updateWhatsAppHubSettings(payload: {
  enabled?: boolean;
  defaultCountryCode?: string;
  defaultLanguageCode?: string;
  ivrTemplateName?: string | null;
}) {
  const response = await api.put<{ success: boolean; data: WhatsAppHubSettings; message?: string }>(
    `${BASE}/meta/settings`,
    payload
  );
  return response.data;
}

export async function fetchWhatsAppWebhookEvents(limit = 50) {
  const response = await api.get<{ success: boolean; data: WhatsAppWebhookEventsResponse }>(
    `${BASE}/meta/webhooks/events`,
    { params: { limit } }
  );
  return response.data;
}

export async function createWhatsAppTemplate(payload: {
  name: string;
  displayName?: string;
  category?: string;
  languageCode?: string;
  bodyPreview?: string;
  variableCount?: number;
  defaultVariableMappings?: WhatsAppTemplateVariableMapping[];
  serviceVertical?: string | null;
  headerFormat?: 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  headerText?: string;
  headerHandleFileUrl?: string;
  headerHandleFileName?: string;
  /** Save to Vedika DB only — do not submit to Interakt */
  draft?: boolean;
  localOnly?: boolean;
}) {
  const response = await api.post<{
    success: boolean;
    data: WhatsAppTemplate;
    message?: string;
    localOnly?: boolean;
    draft?: boolean;
  }>(`${BASE}/templates`, payload);
  return response.data;
}

export type WhatsAppTemplateListOverview = {
  total: number;
  approved: number;
  pending: number;
  rejected: number;
  draft: number;
  synced: number;
  interaktOnly: number;
  dbOnly: number;
};

export type WhatsAppTemplateListMeta = {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  nextOffset: number | null;
  overview: WhatsAppTemplateListOverview;
  filters?: {
    search?: string;
    approvalStatus?: string;
    syncStatus?: string;
    category?: string;
    languageCode?: string;
    headerFormat?: string;
  };
};

export async function fetchWhatsAppTemplateList(opts?: {
  /** Include PENDING, REJECTED, and APPROVED (templates admin page) */
  allStatuses?: boolean;
  /** @deprecated use allStatuses */
  includeInactive?: boolean;
  search?: string;
  approvalStatus?: string;
  status?: string;
  syncStatus?: string;
  inDatabase?: boolean;
  category?: string;
  languageCode?: string;
  headerFormat?: string;
  limit?: number;
  offset?: number;
  page?: number;
  refresh?: boolean;
  paginated?: boolean;
}) {
  const allStatuses = opts?.allStatuses ?? opts?.includeInactive ?? false;
  const usePagination =
    opts?.paginated === true ||
    opts?.limit != null ||
    opts?.offset != null ||
    opts?.page != null ||
    Boolean(opts?.search) ||
    Boolean(opts?.approvalStatus) ||
    Boolean(opts?.status) ||
    Boolean(opts?.syncStatus) ||
    opts?.inDatabase != null ||
    Boolean(opts?.category) ||
    Boolean(opts?.languageCode) ||
    Boolean(opts?.headerFormat);

  const params: Record<string, string | number | boolean> = {};
  if (allStatuses) params.allStatuses = true;
  if (usePagination) {
    params.paginated = true;
    if (opts?.search) params.search = opts.search;
    if (opts?.approvalStatus) params.approvalStatus = opts.approvalStatus;
    if (opts?.status) params.status = opts.status;
    if (opts?.syncStatus) params.syncStatus = opts.syncStatus;
    if (opts?.inDatabase != null) params.inDatabase = opts.inDatabase;
    if (opts?.category) params.category = opts.category;
    if (opts?.languageCode) params.languageCode = opts.languageCode;
    if (opts?.headerFormat) params.headerFormat = opts.headerFormat;
    if (opts?.limit != null) params.limit = opts.limit;
    if (opts?.offset != null) params.offset = opts.offset;
    if (opts?.page != null) params.page = opts.page;
    if (opts?.refresh) params.refresh = true;
  }

  const response = await api.get<{
    success: boolean;
    data: WhatsAppTemplate[];
    meta?: WhatsAppTemplateListMeta;
  }>(`${BASE}/templates`, { params });
  return response.data;
}

export async function getWhatsAppTemplate(id: string) {
  const response = await api.get<{ success: boolean; data: WhatsAppTemplate }>(`${BASE}/templates/${id}`);
  return response.data;
}

export async function updateWhatsAppTemplate(
  id: string,
  payload: {
    name?: string;
    displayName?: string;
    category?: string;
    languageCode?: string;
    bodyPreview?: string;
    variableCount?: number;
    defaultVariableMappings?: WhatsAppTemplateVariableMapping[];
    serviceVertical?: string | null;
    isActive?: boolean;
    footer?: string;
    headerFormat?: 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
    headerText?: string;
    headerHandleFileUrl?: string;
    headerHandleFileName?: string;
    localOnly?: boolean;
    submitToInterakt?: boolean;
  }
) {
  const response = await api.put<{
    success: boolean;
    data: WhatsAppTemplate;
    message?: string;
    localOnly?: boolean;
    submittedToInterakt?: boolean;
  }>(`${BASE}/templates/${id}`, payload);
  return response.data;
}

export async function patchWhatsAppTemplateVariableMappings(
  id: string,
  payload: {
    defaultVariableMappings: WhatsAppTemplateVariableMapping[];
    serviceVertical?: string | null;
  }
) {
  const response = await api.put<{ success: boolean; data: WhatsAppTemplate; message?: string }>(
    `${BASE}/templates/${id}/variable-mappings`,
    payload
  );
  return response.data;
}

export async function deleteWhatsAppTemplate(id: string, opts?: { permanent?: boolean }) {
  const response = await api.delete<{
    success: boolean;
    message?: string;
    interaktDeleted?: boolean;
    localOnly?: boolean;
    interaktDashboardUrl?: string;
  }>(`${BASE}/templates/${id}`, {
    params: opts?.permanent ? { permanent: true } : undefined,
  });
  return response.data;
}

export async function fetchInteraktWhatsAppTemplates(params?: {
  offset?: number;
  templateName?: string;
  approvalStatus?: string;
  language?: string;
}) {
  const response = await api.get<{
    success: boolean;
    data: {
      count: number;
      hasNext: boolean;
      templates: import('../../models/MasterAdmin/whatsappCampaign').InteraktWhatsAppTemplate[];
    };
  }>(`${BASE}/templates/interakt`, { params });
  return response.data;
}

export async function syncInteraktWhatsAppTemplates(opts?: { approvalStatus?: string }) {
  const response = await api.post<{
    success: boolean;
    data: {
      total: number;
      created: number;
      updated: number;
      skipped: number;
      markedMissingOnInterakt?: number;
      deactivated?: number;
    };
    message?: string;
  }>(`${BASE}/templates/interakt/sync`, opts || {});
  return response.data;
}

export async function syncWhatsAppTemplateToDatabase(id: string) {
  const response = await api.post<{
    success: boolean;
    data: { action: string; template: WhatsAppTemplate };
    message?: string;
  }>(`${BASE}/templates/${encodeURIComponent(id)}/sync-to-db`);
  return response.data;
}

export async function submitWhatsAppTemplateToInterakt(
  id: string,
  payload?: {
    name?: string;
    displayName?: string;
    category?: string;
    languageCode?: string;
    bodyPreview?: string;
    variableCount?: number;
    defaultVariableMappings?: WhatsAppTemplateVariableMapping[];
    serviceVertical?: string | null;
    headerFormat?: 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
    headerText?: string;
    headerHandleFileUrl?: string;
    headerHandleFileName?: string;
  }
) {
  const response = await api.post<{
    success: boolean;
    data: WhatsAppTemplate;
    message?: string;
    localOnly?: boolean;
    submittedToInterakt?: boolean;
  }>(`${BASE}/templates/${encodeURIComponent(id)}/submit-to-interakt`, payload || {});
  return response.data;
}

export async function fetchWhatsAppTemplateVersions(id: string) {
  const response = await api.get<{
    success: boolean;
    data: import('../../models/MasterAdmin/whatsappCampaign').WhatsAppTemplateVersionHistoryEntry[];
  }>(`${BASE}/templates/${encodeURIComponent(id)}/versions`);
  return response.data;
}

export async function fetchWhatsAppTemplateDeletionLogs(opts?: { includeRestored?: boolean }) {
  const response = await api.get<{
    success: boolean;
    data: import('../../models/MasterAdmin/whatsappCampaign').WhatsAppTemplateDeletionLog[];
  }>(`${BASE}/templates/deletion-logs`, {
    params: opts?.includeRestored ? { includeRestored: true } : undefined,
  });
  return response.data;
}

export async function restoreWhatsAppTemplateFromLog(logId: string) {
  const response = await api.post<{
    success: boolean;
    data: WhatsAppTemplate;
    message?: string;
  }>(`${BASE}/templates/deletion-logs/${encodeURIComponent(logId)}/restore`);
  return response.data;
}

export async function submitInteraktWhatsAppTemplate(payload: {
  displayName: string;
  name?: string;
  category?: string;
  languageCode?: string;
  body: string;
  bodyPreview?: string;
  bodyText?: string[];
  footer?: string;
  headerFormat?: 'TEXT' | 'IMAGE' | null;
  header?: string;
  headerText?: string[];
  headerHandleFileUrl?: string;
  headerHandleFileName?: string;
  registerLocally?: boolean;
}) {
  const response = await api.post(`${BASE}/templates/interakt/submit`, payload);
  return response.data;
}

export async function fetchWhatsAppNotificationCatalog(params?: {
  period?: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';
  days?: number;
  startDate?: string;
  endDate?: string;
  recipientType?: string;
  service?: string;
  messageCategory?: string;
  whatsappSupported?: boolean;
  search?: string;
}) {
  const response = await api.get<{
    success: boolean;
    data: import('../../models/MasterAdmin/whatsappHub').WhatsAppNotificationCatalog;
  }>(`${BASE}/notifications/catalog`, { params });
  return response.data;
}

export async function fetchWhatsAppNotificationDetail(
  eventKey: string,
  stats?: {
    period?: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';
    days?: number;
    startDate?: string;
    endDate?: string;
  }
) {
  const response = await api.get<{
    success: boolean;
    data: import('../../models/MasterAdmin/whatsappHub').WhatsAppNotificationEvent;
  }>(`${BASE}/notifications/${encodeURIComponent(eventKey)}`, { params: stats });
  return response.data;
}

function toHealthStatus(
  value: boolean | number | string | null
): 'working' | 'not_working' | 'untested' {
  if (value === true || value === 1 || value === '1' || value === 'working' || value === 'true') {
    return 'working';
  }
  if (value === false || value === 0 || value === '0' || value === 'not_working' || value === 'false') {
    return 'not_working';
  }
  return 'untested';
}

export async function updateWhatsAppNotificationEvent(
  eventKey: string,
  payload: {
    enabled?: boolean;
    whatsappEnabled?: boolean;
    templateName?: string | null;
    templateRules?: import('../../models/MasterAdmin/whatsappHub').WhatsAppTemplateRule[];
    languageCode?: string;
    templateVariableMappings?: import('../../models/MasterAdmin/whatsappHub').WhatsAppTemplateVariableMapping[];
    whatsappCtaConfig?: import('../../models/MasterAdmin/whatsappHub').WhatsAppCtaDeepLinkConfig | null;
    headerMediaUrl?: string | null;
    headerMimeType?: string | null;
    headerFileName?: string | null;
    templateWorking?: boolean | number | string | null;
    linkNavigationWorking?: boolean | number | string | null;
  }
) {
  const body = { ...payload };
  if (payload.templateWorking !== undefined) {
    body.templateWorking = toHealthStatus(payload.templateWorking);
  }
  if (payload.linkNavigationWorking !== undefined) {
    body.linkNavigationWorking = toHealthStatus(payload.linkNavigationWorking);
  }
  const response = await api.put<{ success: boolean; message?: string }>(
    `${BASE}/notifications/${encodeURIComponent(eventKey)}`,
    body
  );
  return response.data;
}
